import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Animated,
  Platform,
} from 'react-native';
import {
  Card,
  useTheme,
  Searchbar,
  TextInput,
  Portal,
  Modal,
  Button,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {ActionButton} from '../../shared/components/ActionButton';
import {Beneficiary} from '../../domain/scan/entities/Beneficiary';
import {Service} from '../../domain/scan/entities/Service';
import {ServiceStatus} from '../../domain/scan/entities/ServiceStatus';
import {ServiceContainer} from '../../shared/di/ServiceContainer';
import {MainLayout} from '../layouts/MainLayout';
import {notificationService} from '../../shared/services/notificationService';

interface ServiceSelectionScreenProps {
  navigation: any;
  route: {
    params: {
      beneficiary: Beneficiary;
      qrCode: string;
    };
  };
}

export const ServiceSelectionScreen: React.FC<ServiceSelectionScreenProps> = ({
  navigation,
  route,
}) => {
  const {beneficiary, qrCode} = route.params ?? {};
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [caseDetail, setCaseDetail] = useState<any>(null);
  const [shouldShowScreen, setShouldShowScreen] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [singleServiceForModal, setSingleServiceForModal] = useState<Service | null>(null);
  const [modalNotes, setModalNotes] = useState('');
  const notesHeight = useRef(new Animated.Value(0)).current;
  const theme = useTheme();
  const scanService = ServiceContainer.getInstance().getScanService();

  // Resolve caseId: from beneficiary or by parsing qrCode (in case params were serialized and caseId was lost)
  const caseIdRef = useRef<number | null>(null);
  if (caseIdRef.current === null && (beneficiary || qrCode)) {
    const fromBeneficiary = beneficiary?.caseId;
    const fromQr = qrCode ? parseInt(String(qrCode).trim(), 10) : NaN;
    const id = (typeof fromBeneficiary === 'number' && fromBeneficiary > 0)
      ? fromBeneficiary
      : (Number.isInteger(fromQr) && fromQr > 0 ? fromQr : null);
    if (id != null) caseIdRef.current = id;
  }
  const caseId = caseIdRef.current;

  useEffect(() => {
    if (caseId != null) {
      loadServices(caseId);
      loadCaseDetails(caseId);
    } else {
      setLoading(false);
      notificationService.error(
        'No se pudo obtener el caso del código QR. Escanea de nuevo.'
      );
    }
  }, [caseId]);

  // Reset shouldShowScreen when screen comes into focus
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      // If we're coming back to this screen, show it
      if (!loading && services.length > 0) {
        setShouldShowScreen(true);
      }
    });

    return unsubscribe;
  }, [navigation, loading, services]);

  // Auto-select and navigate if only one service is available
  useEffect(() => {
    if (!loading && services.length > 0) {
      // Check if there's only one active service with available redemptions
      const activeServices = services.filter(
        service =>
          service.status === ServiceStatus.ACTIVE &&
          service.availableRedemptions > 0
      );

      if (activeServices.length === 1 && !shouldShowScreen && !showNotesModal) {
        // Show modal for notes before auto-submitting
        const singleService = activeServices[0];
        setSingleServiceForModal(singleService);
        setShowNotesModal(true);
        setShouldShowScreen(true); // Set this to true to render the screen behind the modal
      } else if (activeServices.length !== 1) {
        // Show the screen if there are multiple services or no active services
        setShouldShowScreen(true);
      }
    } else if (!loading && services.length === 0) {
      // Show the screen even if no services (to show empty state)
      setShouldShowScreen(true);
    }
  }, [loading, services, beneficiary, navigation, shouldShowScreen, showNotesModal, scanService]);

  const loadCaseDetails = async (id: number) => {
    try {
      const detail = await scanService.getCaseDetail(id);
      setCaseDetail(detail);
    } catch (error: any) {
      console.error('Error loading case details:', error);
      // Non-critical error, continue without case details
    }
  };

  const loadServices = async (id: number) => {
    try {
      // Request benefits/assignments from Seli using case_user_id
      const availableServices = await scanService.getAvailableServicesFromEligibility(id);
      setServices(availableServices);
    } catch (error: any) {
      console.error('Error loading services:', error);
      notificationService.error(
        error.message || 'Error al cargar los beneficios disponibles'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service: Service) => {
    if (selectedService?.id === service.id) {
      // Deselect if clicking the same service
      // Animate collapse
      Animated.timing(notesHeight, {
        toValue: 0,
        duration: 300,
        useNativeDriver: false,
      }).start(() => {
        setSelectedService(null);
        setNotes('');
      });
    } else {
      setSelectedService(service);
      setNotes('');
      // Animate expand
      Animated.timing(notesHeight, {
        toValue: 150,
        duration: 300,
        useNativeDriver: false,
      }).start();
    }
  };

  const handleSubmit = async () => {
    if (!selectedService || !caseId) {
      return;
    }

    setSubmitting(true);

    try {
      const serviceWithAssignment = selectedService as any;
      const benefitId = parseInt(selectedService.benefitId || selectedService.id, 10);
      let assignmentId: number = serviceWithAssignment.assignment?.assignmentId;

      // If no assignment yet, create one (ONE_TIME recurrence)
      if (!assignmentId) {
        const newAssignment = await scanService.createAssignment({
          benefitId,
          caseUserId: caseId,
          recurrence: 1, // ONE_TIME
          startDate: new Date().toISOString().split('T')[0],
        });
        assignmentId = newAssignment.id;
      }

      // Create delivery
      const delivery = await scanService.createDelivery({
        assignmentId,
        benefitId,
        caseUserId: caseId,
        deliveryAmount: 1,
      });

      // Mark delivery as completed
      await scanService.updateDelivery({
        deliveryId: delivery.id,
        action: 'mark_completed',
        status: 2,
        notes: notes.trim() || undefined,
      });

      setSubmitting(false);
      navigation.navigate('DeliverySuccess');
    } catch (error: any) {
      setSubmitting(false);
      navigation.navigate('DeliveryError', {
        errorMessage: error.message || 'Error al crear el canje',
      });
    }
  };

  const handleModalConfirm = async () => {
    if (!singleServiceForModal || !caseId) {
      return;
    }

    setSubmitting(true);

    try {
      const serviceWithAssignment = singleServiceForModal as any;
      const benefitId = parseInt(singleServiceForModal.benefitId || singleServiceForModal.id, 10);
      let assignmentId: number = serviceWithAssignment.assignment?.assignmentId;

      // If no assignment yet, create one (ONE_TIME recurrence)
      if (!assignmentId) {
        const newAssignment = await scanService.createAssignment({
          benefitId,
          caseUserId: caseId,
          recurrence: 1, // ONE_TIME
          startDate: new Date().toISOString().split('T')[0],
        });
        assignmentId = newAssignment.id;
      }

      // Create delivery
      const delivery = await scanService.createDelivery({
        assignmentId,
        benefitId,
        caseUserId: caseId,
        deliveryAmount: 1,
      });

      // Mark delivery as completed
      await scanService.updateDelivery({
        deliveryId: delivery.id,
        action: 'mark_completed',
        status: 2,
        notes: modalNotes.trim() || undefined,
      });

      setSubmitting(false);
      setShowNotesModal(false);
      navigation.navigate('DeliverySuccess');
    } catch (error: any) {
      setSubmitting(false);
      setShowNotesModal(false);
      navigation.navigate('DeliveryError', {
        errorMessage: error.message || 'Error al crear el canje',
      });
    }
  };

  const handleModalCancel = () => {
    setShowNotesModal(false);
    setModalNotes('');
    navigation.goBack();
  };

  const filteredServices = services.filter(service => {
    const query = searchQuery.toLowerCase();
    return (
      service.name.toLowerCase().includes(query) ||
      service.description.toLowerCase().includes(query) ||
      service.category.toLowerCase().includes(query)
    );
  });

  if (loading || !shouldShowScreen) {
    return (
      <MainLayout navigation={navigation} title="Seleccionar Beneficio">
        <View style={[styles.loadingContainer, {backgroundColor: theme.colors.background}]}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={[styles.loadingText, {color: theme.colors.onBackground}]}>
            {loading ? 'Cargando beneficios...' : 'Procesando...'}
          </Text>
        </View>
      </MainLayout>
    );
  }

  return (
    <MainLayout navigation={navigation} title="Seleccionar Beneficio">
      <View style={[styles.container, {backgroundColor: theme.colors.background}]}>
        {/* Información del Beneficiario */}
        <Card style={[styles.beneficiaryCard, {backgroundColor: theme.colors.surface}]} mode="elevated">
          <Card.Content style={styles.beneficiaryCardContent}>
            {/* Nombre y Foto en fila superior */}
            <View style={styles.topRow}>
              {/* Avatar o Foto del Beneficiario - Más grande */}
              {caseDetail?.photoUrl || beneficiary.photoUrl ? (
                <View style={styles.beneficiaryPhotoContainer}>
                  <Image 
                    source={{uri: caseDetail?.photoUrl || beneficiary.photoUrl}} 
                    style={styles.beneficiaryPhotoImage}
                    resizeMode="cover"
                  />
                </View>
              ) : (
                <View style={[styles.beneficiaryAvatar, {backgroundColor: theme.colors.primary}]}>
                  <Icon name="person" size={56} color="#fff" />
                </View>
              )}
              
              {/* Nombre e ID del Caso */}
              <View style={styles.nameSection}>
                <Text style={[styles.beneficiaryName, {color: theme.colors.onSurface}]}>
                  {caseDetail ? `${caseDetail.firstName} ${caseDetail.lastName}` : beneficiary.name}
                </Text>
                
                {/* ID del Caso */}
                {caseId != null && (
                  <View style={styles.caseIdRow}>
                    <Icon name="folder" size={18} color={theme.colors.primary} />
                    <Text style={[styles.caseIdText, {color: theme.colors.primary}]}>
                      {caseDetail?.formattedId || `Caso #${caseId}`}
                    </Text>
                  </View>
                )}
              </View>
            </View>

            {/* Información adicional en grid */}
            {caseDetail && (
              <View style={styles.infoGrid}>
                {caseDetail.age && (
                  <View style={styles.infoItem}>
                    <Icon name="cake" size={18} color={theme.colors.primary} />
                    <Text style={[styles.infoLabel, {color: theme.colors.onSurfaceVariant}]}>
                      Edad
                    </Text>
                    <Text style={[styles.infoValue, {color: theme.colors.onSurface}]}>
                      {caseDetail.age} años
                    </Text>
                  </View>
                )}
                {caseDetail.gender && (
                  <View style={styles.infoItem}>
                    <Icon name="person-outline" size={18} color={theme.colors.primary} />
                    <Text style={[styles.infoLabel, {color: theme.colors.onSurfaceVariant}]}>
                      Género
                    </Text>
                    <Text style={[styles.infoValue, {color: theme.colors.onSurface}]}>
                      {caseDetail.gender}
                    </Text>
                  </View>
                )}
                {caseDetail.phoneNumber && (
                  <View style={styles.infoItem}>
                    <Icon name="phone" size={18} color={theme.colors.primary} />
                    <Text style={[styles.infoLabel, {color: theme.colors.onSurfaceVariant}]}>
                      Teléfono
                    </Text>
                    <Text style={[styles.infoValue, {color: theme.colors.onSurface}]}>
                      {caseDetail.phoneNumber}
                    </Text>
                  </View>
                )}
              </View>
            )}
            
            {/* Número de Documento (fallback) */}
            {!caseDetail && beneficiary.documentNumber && (
              <View style={styles.fallbackInfo}>
                <Icon name="badge" size={18} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.detailText, {color: theme.colors.onSurfaceVariant}]}>
                  Doc: {beneficiary.documentNumber}
                </Text>
              </View>
            )}
          </Card.Content>
        </Card>

        {/* Búsqueda */}
        <Searchbar
          placeholder="Buscar beneficio..."
          onChangeText={setSearchQuery}
          value={searchQuery}
          style={styles.searchbar}
        />

        {/* Lista de Servicios */}
        <ScrollView
          style={styles.servicesList}
          contentContainerStyle={styles.servicesListContent}
          showsVerticalScrollIndicator={false}>
          {filteredServices.length > 0 ? (
            filteredServices.map(service => (
              <Card
                key={service.id}
                style={[
                  styles.serviceCard,
                  {backgroundColor: theme.colors.surface},
                  (service.status !== ServiceStatus.ACTIVE || service.availableRedemptions === 0) && {
                    opacity: 0.6,
                  },
                  selectedService?.id === service.id && {
                    borderWidth: 2,
                    borderColor: theme.colors.primary,
                    backgroundColor: theme.dark
                      ? 'rgba(99, 102, 241, 0.2)'
                      : '#F0F4FF',
                  },
                ]}
                mode="elevated">
                <Card.Content>
                  <TouchableOpacity
                    onPress={() => handleServiceSelect(service)}
                    activeOpacity={0.7}
                    disabled={service.status !== ServiceStatus.ACTIVE || service.availableRedemptions === 0}>
                    <View style={styles.serviceHeader}>
                      <View style={styles.serviceInfo}>
                        <Text style={[styles.serviceName, {color: theme.colors.onSurface}]}>
                          {service.name}
                        </Text>
                        <Text style={[styles.serviceDescription, {color: theme.colors.onSurfaceVariant}]}>
                          {service.providerName}
                        </Text>
                      </View>
                      {selectedService?.id === service.id && (
                        <View style={styles.selectedIndicator}>
                          <Icon name="check-circle" size={32} color={theme.colors.primary} />
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Notes input - shown when service is selected */}
                  {selectedService?.id === service.id && (
                    <Animated.View
                      style={[
                        styles.notesContainer,
                        {
                          height: notesHeight,
                          opacity: notesHeight.interpolate({
                            inputRange: [0, 150],
                            outputRange: [0, 1],
                          }),
                          overflow: 'hidden',
                        }
                      ]}>
                      <Text style={[styles.notesLabel, {color: theme.colors.onSurface}]}>
                        Notas (opcional)
                      </Text>
                      <TextInput
                        mode="outlined"
                        placeholder="Agrega notas sobre esta entrega..."
                        placeholderTextColor={theme.colors.onSurfaceVariant}
                        value={notes}
                        onChangeText={setNotes}
                        multiline
                        numberOfLines={3}
                        style={styles.notesInput}
                        disabled={submitting}
                      />
                    </Animated.View>
                  )}
                </Card.Content>
              </Card>
            ))
          ) : (
            <Card style={[styles.emptyCard, {backgroundColor: theme.colors.surface}]} mode="elevated">
              <Card.Content style={styles.emptyContent}>
                <Icon name="find-in-page" size={48} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.emptyText, {color: theme.colors.onSurfaceVariant}]}>
                  No se encontraron beneficios
                </Text>
                <Text style={[styles.emptySubtext, {color: theme.colors.onSurfaceVariant}]}>
                  Intenta con otros términos de búsqueda
                </Text>
              </Card.Content>
            </Card>
          )}
        </ScrollView>

        {/* Botones de Acción */}
        <View style={[
          styles.footer,
          {backgroundColor: theme.colors.background, borderTopColor: theme.colors.outline},
          Platform.OS === 'web' && styles.footerWeb,
        ]}>
          <ActionButton
            icon="arrow-back"
            label="Volver al Escáner"
            variant="outlined"
            onPress={() => navigation.goBack()}
            disabled={submitting}
            flex={1}
          />
          {selectedService && (
            <ActionButton
              icon="check-circle"
              label={submitting ? 'Procesando...' : 'Confirmar Entrega'}
              variant="contained"
              onPress={handleSubmit}
              disabled={submitting}
              loading={submitting}
              flex={2}
            />
          )}
        </View>
      </View>

      {/* Modal for notes when single service */}
      <Portal>
        <Modal
          visible={showNotesModal}
          onDismiss={handleModalCancel}
          contentContainerStyle={[
            styles.modalContainer,
            {backgroundColor: theme.colors.surface}
          ]}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="card-giftcard" size={32} color={theme.colors.primary} />
              <Text style={[styles.modalTitle, {color: theme.colors.onSurface}]}>
                Confirmar Entrega
              </Text>
              <Text style={[styles.modalSubtitle, {color: theme.colors.onSurfaceVariant}]}>
                Este beneficiario tiene un solo beneficio disponible
              </Text>
            </View>

            {singleServiceForModal && (
              <View style={styles.modalBenefitInfo}>
                <Text style={[styles.modalBenefitLabel, {color: theme.colors.onSurfaceVariant}]}>
                  Beneficio:
                </Text>
                <Text style={[styles.modalBenefitName, {color: theme.colors.onSurface}]}>
                  {singleServiceForModal.name}
                </Text>
                <Text style={[styles.modalBenefitProvider, {color: theme.colors.onSurfaceVariant}]}>
                  {singleServiceForModal.providerName}
                </Text>
              </View>
            )}

            <Text style={[styles.modalNotesLabel, {color: theme.colors.onSurface}]}>
              Notas (opcional)
            </Text>
            <TextInput
              mode="outlined"
              placeholder="Agrega notas sobre esta entrega..."
              placeholderTextColor={theme.colors.onSurfaceVariant}
              value={modalNotes}
              onChangeText={setModalNotes}
              multiline
              numberOfLines={3}
              style={styles.modalNotesInput}
              disabled={submitting}
            />

            <View style={styles.modalActions}>
              <Button
                mode="outlined"
                onPress={handleModalCancel}
                disabled={submitting}
                style={styles.modalButton}>
                Cancelar
              </Button>
              <Button
                mode="contained"
                onPress={handleModalConfirm}
                loading={submitting}
                disabled={submitting}
                style={styles.modalButton}>
                Confirmar
              </Button>
            </View>
          </View>
        </Modal>
      </Portal>
    </MainLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 20,
  },
  beneficiaryCard: {
    margin: 16,
    marginBottom: 12,
    elevation: 4,
  },
  beneficiaryCardContent: {
    padding: 16,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  beneficiaryAvatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 3,
  },
  beneficiaryPhotoContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    borderWidth: 3,
    borderColor: '#fff',
    elevation: 3,
    overflow: 'hidden',
  },
  beneficiaryPhotoImage: {
    width: '100%',
    height: '100%',
  },
  nameSection: {
    flex: 1,
    justifyContent: 'center',
  },
  beneficiaryName: {
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
    lineHeight: 34,
  },
  caseIdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  caseIdText: {
    fontSize: 20,
    fontWeight: '600',
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  infoItem: {
    flex: 1,
    minWidth: '30%',
    alignItems: 'center',
    padding: 8,
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
    borderRadius: 8,
  },
  infoLabel: {
    fontSize: 14,
    marginTop: 4,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  infoValue: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 2,
  },
  fallbackInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  detailText: {
    fontSize: 18,
    lineHeight: 24,
  },

  searchbar: {
    marginHorizontal: 16,
    marginBottom: 12,
  },
  servicesList: {
    flex: 1,
    paddingHorizontal: 16,
  },
  servicesListContent: {
    paddingBottom: Platform.OS === 'web' ? 120 : 16,
  },
  serviceCard: {
    marginBottom: 12,
  },
  serviceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  serviceInfo: {
    flex: 1,
  },
  serviceName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  serviceDescription: {
    fontSize: 18,
    lineHeight: 24,
  },
  selectedIndicator: {
    marginLeft: 12,
  },
  notesContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.08)',
  },
  notesLabel: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  notesInput: {
    minHeight: 80,
    backgroundColor: '#fff',
  },
  emptyCard: {
    marginTop: 32,
  },
  emptyContent: {
    alignItems: 'center',
    paddingVertical: 32,
  },
  emptyText: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 18,
    marginTop: 8,
  },
  footer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    borderTopWidth: 1,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: -2},
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 10,
    flexDirection: 'row',
    gap: 12,
  },
  footerWeb: {
    position: 'fixed' as any,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  modalContainer: {
    margin: 20,
    borderRadius: 16,
    padding: 24,
    maxHeight: '80%',
  },
  modalContent: {
    gap: 16,
  },
  modalHeader: {
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 18,
    textAlign: 'center',
    marginTop: 4,
    lineHeight: 24,
  },
  modalBenefitInfo: {
    padding: 16,
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    borderRadius: 12,
    gap: 4,
  },
  modalBenefitLabel: {
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  modalBenefitName: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  modalBenefitProvider: {
    fontSize: 18,
    marginTop: 2,
  },
  modalNotesLabel: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 8,
  },
  modalNotesInput: {
    minHeight: 100,
    backgroundColor: '#fff',
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  modalButton: {
    flex: 1,
  },
});

